import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "../redux/store";
import tinycolor from "tinycolor2";

import CarPicker from "../CarPicker";

import { updateAppointment } from "../redux/slices/appointmentSlice";
import { captureBooking, sendToWindowDataLayer } from "../utils";
import {
  buildCustomerDraft,
  getMissingRequiredCustomerFields,
  isInvalidEmail,
} from "../utils/customer";
import { ensureBookingId } from "../utils/bailout";
import { scrollIntoViewSmooth } from "../utils/dom";
import { getAppointmentTheme } from "../utils/theme";
import {
  fetchDeferredVehicleServices,
  getRequiredVehicleSelectionFields,
  lookupExistingCustomer,
  useVehicleSelectionCustomerForm,
} from "./vehicleSelection.helpers";

const VehicleSelection = ({ appointment }) => {
  const [loading, setLoading] = useState(true);
  const [, setCarErrors] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [deferredServices, setDeferredServices] = useState([]);
  const [vehicleReset, setVehicleReset] = useState(false);
  const [existingCustomer, setExistingCustomer] = useState(false);
  const [addVehicle, setAddVehicle] = useState(false);
  const [showExistingCustomerDetailsForm, setShowExistingCustomerDetailsForm] =
    useState(false);

  const deferredServicesRef = useRef(null);
  const shouldScrollDeferredServicesRef = useRef(false);
  const dispatch = useDispatch();
  const {
    addressFields,
    customer,
    fieldErrors,
    flagFieldErrors,
    formFields,
    handleFieldChange,
    handleFocus,
    hasFieldError,
    setCustomer,
    setValueCheck,
  } = useVehicleSelectionCustomerForm({
    appointment,
    dispatch,
  });

  const { accentColor, gradientBackground } = useMemo(
    () => getAppointmentTheme(appointment),
    [appointment]
  );

  useEffect(() => {
    let config = {
      domain: appointment?.domain,
      shopId: appointment?.shopData?.shopId,
    };

    ensureBookingId({
      appointment,
      payload: appointment,
      dispatch,
      updateAppointmentAction: updateAppointment,
    })
      .then((id) => {
        dispatch(
          updateAppointment({
            updates: {
              bookingId: appointment.bookingId || id,
              bailed: false,
            },
          })
        );
        return { insertedId: id };
      })
      .then(async (res) => {
        const lookupCustomer = await lookupExistingCustomer(
          appointment?.customer?.phone,
          config
        );

        setExistingCustomer(Boolean(lookupCustomer));
        setLoading(false);

        if (lookupCustomer) {
          const hydratedCustomer = buildCustomerDraft(lookupCustomer);
          // The stored email may be a placeholder ("N/A") or otherwise malformed.
          // When it is, force the customer to supply a valid one before Confirm,
          // and surface the email field even on shops that don't require email.
          const emailInvalid = isInvalidEmail(lookupCustomer?.email);
          const missingRequiredFields =
            getMissingRequiredCustomerFields(
              hydratedCustomer,
              appointment?.shopData
            );
          const fieldsNeedingInput = emailInvalid
            ? Array.from(new Set([...missingRequiredFields, "email"]))
            : missingRequiredFields;
          // Only names are trusted as-is: a non-empty stored fname/lname is
          // treated as prefilled so an oddly-formatted value (e.g. a last name
          // with a digit) does not block Confirm. Editing the field later clears
          // it from this list. Address fields are NOT trusted — buildCustomerDraft
          // blanks any format-failing city/state/zip so they are re-collected.
          const prefilledCustomerFields = [
            ["fname", hydratedCustomer.fname],
            ["lname", hydratedCustomer.lname],
          ]
            .filter(([, value]) => Boolean((value || "").trim()))
            .map(([field]) => field);

          setCustomer(hydratedCustomer);
          setValueCheck(fieldsNeedingInput);
          flagFieldErrors(fieldsNeedingInput, hydratedCustomer);
          setShowExistingCustomerDetailsForm(fieldsNeedingInput.length > 0);
          setVehicles(lookupCustomer.vehicles);

          dispatch(
            updateAppointment({
              updates: {
                customerId: lookupCustomer.integrationId,
                dcId: lookupCustomer._id,
                requireValidCustomerEmail: emailInvalid,
                prefilledCustomerFields,
                customer: {
                  ...appointment.customer,
                  fname: hydratedCustomer.fname,
                  lname: hydratedCustomer.lname,
                  email: hydratedCustomer.email,
                  address: {
                    street: hydratedCustomer.address.street,
                    city: hydratedCustomer.address.city,
                    state: hydratedCustomer.address.state,
                    zip: hydratedCustomer.address.zip,
                  },
                },
              },
            })
          );

          const updatedAppointment = {
            ...appointment,
            bookingId: appointment.bookingId || res.insertedId,
            bailed: false,
            customerId: lookupCustomer.integrationId,
            dcId: lookupCustomer._id,
            requireValidCustomerEmail: emailInvalid,
            prefilledCustomerFields,
            customer: {
              ...appointment.customer,
              fname: hydratedCustomer.fname,
              lname: hydratedCustomer.lname,
              email: hydratedCustomer.email,
              address: {
                street: hydratedCustomer.address.street,
                city: hydratedCustomer.address.city,
                state: hydratedCustomer.address.state,
                zip: hydratedCustomer.address.zip,
              },
            },
          };
          captureBooking(updatedAppointment);
          sendToWindowDataLayer(
            "dc-service-phone_number",
            appointment?.customer?.phone
          );
        }
      })
      .catch((error) => {
        console.error("Error during booking process:", error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setValueCheck(getRequiredVehicleSelectionFields(appointment?.shopData));
  }, [
    appointment?.shopData?.requireAddress,
    appointment?.shopData?.requireNewCustomerEmail,
  ]);

  useEffect(() => {
    if (selectedVehicle) {
      const config = {
        domain: appointment?.domain,
        shopId: appointment?.shopData?.shopId,
      };

      const customer = {
        _id: appointment.dcId,
        vehicle_id: selectedVehicle?.crossRef[0]?.crossRef,
      };

      if (appointment?.vehicle?.crossRef.length > 0) {
        fetchDeferredVehicleServices(customer, config).then((lineItems) => {
          if (lineItems.length > 0) {
            setDeferredServices(lineItems);
          } else {
            dispatch(
              updateAppointment({
                updates: {
                  deferredServices: [],
                },
              })
            );
            setDeferredServices([]);
          }
        });
      } else {
        dispatch(
          updateAppointment({
            updates: {
              deferredServices: [],
            },
          })
        );
        setDeferredServices([]);
      }
    }
  }, [selectedVehicle]);

  useEffect(() => {
    shouldScrollDeferredServicesRef.current = Boolean(selectedVehicle);
  }, [selectedVehicle]);

  useEffect(() => {
    if (
      selectedVehicle &&
      shouldScrollDeferredServicesRef.current &&
      deferredServices.length > 0
    ) {
      scrollIntoViewSmooth(deferredServicesRef.current);
      shouldScrollDeferredServicesRef.current = false;
    }
  }, [deferredServices, selectedVehicle]);
  const clearVehicleSelection = () => {
    setSelectedVehicle(null);
    setVehicleReset(false);
    dispatch(
      updateAppointment({
        updates: {
          vehicle: {
            year: "",
            make: "",
            model: "",
            crossRef: [],
            id: vehicles?.length,
          },
        },
      })
    );
  };

  // Enter "add a new vehicle" mode: hide the existing list and deselect any
  // previously chosen vehicle so the customer only completes one path.
  const handleAddVehicle = () => {
    clearVehicleSelection();
    setAddVehicle(true);
  };

  // Return to the existing-vehicle list, discarding the in-progress new vehicle.
  const handleChooseExistingVehicle = () => {
    clearVehicleSelection();
    setAddVehicle(false);
  };

  // Render a single inline form field matching the new UI pattern
  const renderFormField = (field, isAddress = false) => {
    const fieldValue = isAddress
      ? customer?.address?.[field.name] || ""
      : customer[field.name] || "";

    return (
      <div key={field.name} className="flex flex-col">
        <label
          htmlFor={field.name}
          className="text-sm font-semibold mb-1"
          style={{ color: "#1f2937" }}
        >
          {field.label}
          {fieldErrors[field.name] && (
            <span className="text-red-500 text-sm font-normal ml-1">
              ({fieldErrors[field.name]})
            </span>
          )}
        </label>
        <input
          type={field.type}
          name={field.name}
          id={field.name}
          placeholder={field.placeholder}
          required={field.required}
          value={fieldValue}
          maxLength={field.name === "phone" ? 20 : 100}
          className="w-full box-border p-2 border-2 rounded-lg text-sm"
          style={{
            border: hasFieldError(field.name)
              ? "2px solid #ef4444"
              : `2px solid ${accentColor}66`,
            color: "#111827",
            backgroundColor: "#ffffff",
            outlineColor: accentColor,
          }}
          onFocus={() => handleFocus(field.name)}
          onChange={(e) => handleFieldChange(e, field.required)}
        />
      </div>
    );
  };

  return (
    <div
      className="px-4 py-5 md:px-6 md:pt-2 md:pb-0 min-h-full font-roboto"
      style={{ background: gradientBackground }}
    >
      <div className="md:max-h-[68vh] md:overflow-y-auto md:pr-1 md:pb-2">
      {/* Loading state */}
      {loading && (
        <div
          className="rounded-2xl bg-white p-5"
          style={{
            border: `2px solid ${accentColor}33`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <div className="flex justify-center items-center py-12">
            <div className="flex flex-col items-center justify-center text-center">
              <i
                className="fa-solid fa-tire-rugged animate-spin text-6xl"
                aria-hidden="true"
                style={{ color: "#6b6b6b", lineHeight: 1 }}
              />
              <p className="text-sm text-gray-600 mt-3">
                Fetching your information...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Existing customer view */}
      {!loading && existingCustomer && (
        <div className="flex flex-col gap-4">
          {/* Welcome card */}
          <div
            className="rounded-2xl bg-white p-5"
            style={{
              border: `2px solid ${accentColor}33`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <h2
              className="text-lg font-bold mb-1"
              style={{ color: "#1f2937" }}
            >
              {appointment?.customer?.fname?.trim?.()
                ? `Welcome Back, ${appointment.customer.fname.trim()}!`
                : "Welcome Back!"}
            </h2>

            {showExistingCustomerDetailsForm && (
              <div
                className="mb-4 p-3 rounded-xl text-sm"
                style={{
                  backgroundColor: tinycolor("#ef4444")
                    .setAlpha(0.1)
                    .toRgbString(),
                  color: "#ef4444",
                }}
              >
                Some customer details are missing or invalid. Please update them
                below to continue.
              </div>
            )}

            {showExistingCustomerDetailsForm && (
              <div className="flex flex-col gap-4 mb-6">
                {[formFields[0], formFields[1]]
                  .concat(
                    appointment?.shopData?.requireNewCustomerEmail ||
                      appointment?.requireValidCustomerEmail
                      ? [formFields[2]]
                      : []
                  )
                  .map((field) => renderFormField(field, false))}

                {appointment?.shopData?.requireAddress &&
                  addressFields.map((field) => renderFormField(field, true))}
              </div>
            )}

            {vehicles?.length === 0 ? (
              <CarPicker
                vehicleId={vehicles?.length}
                appointment={appointment}
                selectedVehicle={selectedVehicle}
                setSelectedVehicle={setSelectedVehicle}
                vehicleReset={vehicleReset}
                setVehicleReset={setVehicleReset}
                setDeferredServices={setDeferredServices}
                setCarErrors={setCarErrors}
              />
            ) : addVehicle ? (
              <>
                <h3
                  className="text-sm font-bold mb-3"
                  style={{ color: "#1f2937" }}
                >
                  Adding a new vehicle
                </h3>
                <CarPicker
                  vehicleId={vehicles?.length}
                  appointment={appointment}
                  selectedVehicle={selectedVehicle}
                  setSelectedVehicle={setSelectedVehicle}
                  vehicleReset={vehicleReset}
                  setVehicleReset={setVehicleReset}
                  setDeferredServices={setDeferredServices}
                  setCarErrors={setCarErrors}
                />
                <div className="flex justify-center mt-4">
                  <button
                    type="button"
                    className="cursor-pointer bg-transparent border-none text-sm font-semibold underline transition-opacity hover:opacity-80 focus:outline-none"
                    style={{ color: accentColor }}
                    onClick={() => handleChooseExistingVehicle()}
                  >
                    ← Choose one of your vehicles instead
                  </button>
                </div>
              </>
            ) : (
              <>
            <p
              className="text-xs mb-4"
              style={{ color: "#6b7280" }}
            >
              Select one
            </p>

            <div className="grid gap-3 md:grid-cols-2">
              {vehicles?.map((vehicle, index) => {
                const isSelected =
                  selectedVehicle?.crossRef?.[0]?.crossRef ===
                  vehicle?.crossRef?.[0]?.crossRef;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-3 rounded-[16px] p-3 cursor-pointer transition-shadow font-roboto"
                    onClick={() => {
                      setSelectedVehicle(vehicle);
                      setVehicleReset(false);
                      dispatch(
                        updateAppointment({
                          updates: { vehicle: vehicle },
                        })
                      );

                    }}
                    style={{
                      backgroundColor: isSelected ? accentColor : "#ffffff",
                      border: `2px solid ${isSelected ? accentColor : `${accentColor}44`}`,
                      boxShadow: isSelected
                        ? `0 6px 16px ${accentColor}55`
                        : "0 1px 4px rgba(0,0,0,0.08)",
                    }}
                  >
                    {/* Checkbox */}
                    <div className="flex-shrink-0 self-center">
                      <div
                        className="flex items-center justify-center w-7 h-7 rounded-md"
                        style={{
                          border: `2px solid ${accentColor}`,
                          backgroundColor: isSelected
                            ? "rgba(255,255,255,0.25)"
                            : "#f3f4f6",
                        }}
                      >
                        {isSelected && (
                          <i
                            className="fa-solid fa-check"
                            aria-hidden="true"
                            style={{
                              color: "#ffffff",
                              fontSize: "14px",
                              lineHeight: 1,
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Vehicle info */}
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm font-bold leading-tight"
                        style={{
                          color: isSelected ? "#ffffff" : accentColor,
                        }}
                      >
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </div>
                    </div>

                    {/* Car icon */}
                    <div className="flex-shrink-0">
                      <i
                        className="fa-solid fa-car"
                        aria-hidden="true"
                        style={{
                          color: isSelected
                            ? "rgba(255,255,255,0.7)"
                            : `${accentColor}66`,
                          fontSize: "20px",
                          lineHeight: 1,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

                <div className="flex items-center my-4">
                  <div className="flex-grow h-px bg-gray-300"></div>
                  <span
                    className="px-4 text-sm font-medium"
                    style={{ color: "#6b7280" }}
                  >
                    OR
                  </span>
                  <div className="flex-grow h-px bg-gray-300"></div>
                </div>

                <div className="flex justify-center">
                  <button
                    type="button"
                    className="cursor-pointer rounded-lg border-2 border-solid px-5 py-2 text-sm font-semibold transition-opacity hover:opacity-80 active:opacity-70 focus:outline-none"
                    style={{
                      backgroundColor: "#ffffff",
                      borderColor: accentColor,
                      color: accentColor,
                      boxShadow: "none",
                    }}
                    onClick={() => handleAddVehicle()}
                  >
                    Add New Vehicle
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Deferred services card */}
          {deferredServices?.length > 0 && !appointment?.vehicleId && (
            <div
              ref={deferredServicesRef}
              className="rounded-2xl bg-white p-5"
              style={{
                border: `2px solid ${accentColor}33`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              {/* Section header */}
              <div className="mb-4">
                <h3
                  className="text-sm font-semibold leading-tight"
                  style={{ color: "#1f2937" }}
                >
                  Previously Declined Services
                </h3>
                <p
                  className="text-xs mt-0.5"
                  style={{ color: "#6b7280" }}
                >
                  Would you like to add any of these? (select multiple)
                </p>
              </div>

              {/* Service items */}
              <div className="flex flex-col gap-3">
                {deferredServices.map((service, index) => {
                  const isSelected =
                    appointment?.deferredServices?.includes(
                      service.description
                    );
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 rounded-[16px] p-3 cursor-pointer transition-shadow"
                      onClick={() => {
                        dispatch(
                          updateAppointment({
                            updates: {
                              deferredServices: isSelected
                                ? (
                                    appointment?.deferredServices || []
                                  ).filter(
                                    (id) => id !== service.description
                                  )
                                : [
                                    ...(appointment?.deferredServices ||
                                      []),
                                    service.description,
                                  ],
                            },
                          })
                        );
                      }}
                      style={{
                        backgroundColor: isSelected
                          ? accentColor
                          : "#ffffff",
                        border: `2px solid ${isSelected ? accentColor : `${accentColor}44`}`,
                        boxShadow: isSelected
                          ? `0 6px 16px ${accentColor}55`
                          : "0 1px 4px rgba(0,0,0,0.08)",
                      }}
                    >
                      {/* Checkbox */}
                      <div className="flex-shrink-0 self-center">
                        <div
                          className="flex items-center justify-center w-7 h-7 rounded-md"
                          style={{
                            border: `2px solid ${accentColor}`,
                            backgroundColor: isSelected
                              ? "rgba(255,255,255,0.25)"
                              : "#f3f4f6",
                          }}
                        >
                          {isSelected && (
                            <i
                              className="fa-solid fa-check"
                              aria-hidden="true"
                              style={{
                                color: "#ffffff",
                                fontSize: "14px",
                                lineHeight: 1,
                              }}
                            />
                          )}
                        </div>
                      </div>

                      {/* Service info */}
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-sm font-bold leading-tight"
                          style={{
                            color: isSelected ? "#ffffff" : "#1f2937",
                          }}
                        >
                          {service.description}
                        </div>
                      </div>

                      {/* Wrench icon */}
                      <div className="flex-shrink-0">
                        <i
                          className="fa-solid fa-wrench"
                          aria-hidden="true"
                          style={{
                            color: isSelected
                              ? "rgba(255,255,255,0.7)"
                              : `${accentColor}66`,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* New customer view */}
      {!loading && !existingCustomer && (
        <div className="flex flex-col gap-4">
          {/* Customer info card */}
          <div
            className="rounded-2xl bg-white p-5"
            style={{
              border: `2px solid ${accentColor}33`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <h2
              className="text-lg font-bold mb-4 text-center"
              style={{ color: "#1f2937" }}
            >
              Looks like you're a new customer! 👋
            </h2>

            {Object.keys(fieldErrors).length > 0 && (
              <div
                className="mb-4 p-3 rounded-xl text-sm"
                style={{
                  backgroundColor: tinycolor("#ef4444")
                    .setAlpha(0.1)
                    .toRgbString(),
                  color: "#ef4444",
                }}
              >
                <ul className="list-disc list-inside">
                  {Object.keys(fieldErrors).map((error, index) => (
                    <li key={index}>{fieldErrors[error]}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-col gap-4">
              {formFields.map((field) => renderFormField(field, false))}
            </div>

            {appointment?.shopData?.requireAddress && (
              <div className="flex flex-col gap-4 mt-6">
                <h3
                  className="text-sm font-bold"
                  style={{ color: "#1f2937" }}
                >
                  Address
                </h3>
                {addressFields.map((field) => renderFormField(field, true))}
              </div>
            )}
          </div>

          {/* Vehicle info card */}
          <div
            className="rounded-2xl bg-white p-5"
            style={{
              border: `2px solid ${accentColor}33`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <p
              className="text-sm mb-4"
              style={{ color: "#1f2937" }}
            >
              Please enter your vehicle information below. If you don't see your
              make, model, or year simply type it in.
            </p>

            <CarPicker
              vehicleId={vehicles?.length}
              appointment={appointment}
              selectedVehicle={selectedVehicle}
              setSelectedVehicle={setSelectedVehicle}
              vehicleReset={vehicleReset}
              setVehicleReset={setVehicleReset}
              setDeferredServices={setDeferredServices}
              setCarErrors={setCarErrors}
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export { VehicleSelection };
